import { canais } from "../utils/canais"

export const ticketEhSemResponsavel = (ticket) =>
  ticket?.responsavelId == null && ticket?.status === "pending"

export const ticketSemResponsavelStatusInconsistente = (ticket) =>
  ticket?.responsavelId == null && ticket?.status === "open"

/** Inclui inconsistentes (open sem responsável) para não perder conversas até a API normalizar. */
export const ticketEhSemResponsavelAtivo = (ticket) =>
  ticketEhSemResponsavel(ticket) || ticketSemResponsavelStatusInconsistente(ticket)

export const ticketSemResponsavelStatusEmMemoriaNormalizar = (ticket) => {
  if (!ticketSemResponsavelStatusInconsistente(ticket)) return ticket
  return { ...ticket, status: "pending" }
}

/** Monta objeto responsável mínimo para exibição nos cards do Kanban. */
export const objetoResponsavelMontar = (oUsuario) => {
  if (!oUsuario?.id) return null
  return {
    id: oUsuario.id,
    name: oUsuario.name,
    nomeAbreviacao: oUsuario.nomeAbreviacao,
  }
}

const relacaoMescladaResolver = (oAnterior, oNovo, cChaveRelacao, cChaveId) => {
  // ID explícito tem prioridade — evita flicker quando a API envia responsavelId null com objeto legado.
  if (oNovo?.[cChaveId] !== undefined) {
    if (oNovo[cChaveId] == null) return null
    if (oNovo?.[cChaveRelacao]) {
      return mergePreservandoNaoNulos(oAnterior?.[cChaveRelacao], oNovo[cChaveRelacao])
    }
    if (oAnterior?.[cChaveRelacao]?.id === oNovo[cChaveId]) {
      return oAnterior[cChaveRelacao]
    }
    return null
  }

  if (oNovo?.[cChaveRelacao]) {
    return mergePreservandoNaoNulos(oAnterior?.[cChaveRelacao], oNovo[cChaveRelacao])
  }

  return oAnterior?.[cChaveRelacao]
}

const ticketParaMesclagemNomalizar = (oTicket) => {
  if (!oTicket) return oTicket
  const oNormalizado = { ...oTicket }
  // Só null explícito limpa o responsável — payload parcial sem a chave preserva o anterior.
  if (oNormalizado.responsavelId === null) {
    oNormalizado.responsavel = null
  }
  return oNormalizado
}

export const mergePreservandoNaoNulos = (oAntigo, oNovo) => {
  if (!oAntigo || !oNovo) return oNovo || oAntigo

  const oResultado = { ...oAntigo, ...oNovo }
  Object.keys(oNovo).forEach((cChave) => {
    if (oNovo[cChave] == null && oAntigo[cChave] != null) {
      oResultado[cChave] = oAntigo[cChave]
    }
  })

  return oResultado
}

/**
 * Indica que o payload pede remoção explícita da fila (queueId null presente no objeto).
 * Usado após exclusão de fila / update de ticket sem fila.
 */
export const ticketPayloadRemoveFila = (oTicket) =>
  Boolean(oTicket && Object.prototype.hasOwnProperty.call(oTicket, "queueId") && oTicket.queueId == null)

/**
 * Limpa fila de tickets em memória cujo queueId (ou queue.id) coincide com a fila excluída.
 * Retorna o mesmo mapa se nada mudou.
 */
export const ticketsRemoverFilaPorId = (oTicketsById, nFilaId) => {
  if (!oTicketsById || nFilaId == null) return oTicketsById

  const nId = Number(nFilaId)
  if (Number.isNaN(nId)) return oTicketsById

  let lAlterou = false
  const oResultado = { ...oTicketsById }

  Object.keys(oResultado).forEach((cChave) => {
    const oTicket = oResultado[cChave]
    if (!oTicket) return
    const nQueueIdTicket = oTicket.queueId != null ? Number(oTicket.queueId) : null
    const nQueueIdNested = oTicket.queue?.id != null ? Number(oTicket.queue.id) : null
    if (nQueueIdTicket === nId || nQueueIdNested === nId) {
      oResultado[cChave] = { ...oTicket, queueId: null, queue: null }
      lAlterou = true
    }
  })

  return lAlterou ? oResultado : oTicketsById
}

export const calcularMensagensNaoLidas = (oTicket, nUserId) => {
  if (!oTicket) return 0

  if (oTicket.canalTipoId === canais.interno) {
    return Array.isArray(oTicket.conversaParticipantes)
      ? oTicket.conversaParticipantes.find((p) => p.usuarioId === nUserId)?.mensagensNaoLidas || 0
      : 0
  }

  return oTicket.unreadMessages ?? 0
}

/** Indica se o ticket tem mensagens não lidas para o usuário (WhatsApp ou conversa interna). */
export const ticketTemMensagensNaoLidas = (oTicket, user) => {
  if (!oTicket || user?.id == null) return false
  return calcularMensagensNaoLidas(oTicket, user.id) > 0
}

export const mesclarTicket = (oAnterior, oNovo, nUserId) => {
  const lFilaRemovida = ticketPayloadRemoveFila(oNovo)
  const oNovoNormalizado = ticketParaMesclagemNomalizar(oNovo)
  const oBase = {
    ...oAnterior,
    ...oNovoNormalizado,
    responsavel: relacaoMescladaResolver(oAnterior, oNovoNormalizado, "responsavel", "responsavelId"),
    usuario: oNovoNormalizado?.usuario
      ? mergePreservandoNaoNulos(oAnterior?.usuario, oNovoNormalizado.usuario)
      : oAnterior?.usuario,
    contact: oNovoNormalizado?.contact
      ? mergePreservandoNaoNulos(oAnterior?.contact, oNovoNormalizado.contact)
      : oAnterior?.contact,
    queue: lFilaRemovida
      ? null
      : oNovoNormalizado?.queue
        ? mergePreservandoNaoNulos(oAnterior?.queue, oNovoNormalizado.queue)
        : oAnterior?.queue,
    situacao: relacaoMescladaResolver(oAnterior, oNovoNormalizado, "situacao", "situacaoId"),
    motivoContato: oNovoNormalizado?.motivoContato
      ? mergePreservandoNaoNulos(oAnterior?.motivoContato, oNovoNormalizado.motivoContato)
      : oAnterior?.motivoContato,
    conversaParticipantes:
      oNovoNormalizado?.conversaParticipantes || oAnterior?.conversaParticipantes || [],
  }

  if (lFilaRemovida) {
    oBase.queueId = null
    oBase.queue = null
  }

  return {
    ...oBase,
    unreadMessages: calcularMensagensNaoLidas(oBase, nUserId),
  }
}

/**
 * Mescla um contato atualizado dentro de um ticket, gerando NOVA referência de ticket
 * (necessário para o React.memo dos cards re-renderizar). Normaliza nome/imagem para os
 * dois formatos de payload (listagem: nome/imagemPerfil; update: name/profilePicUrl).
 * Retorna o mesmo ticket quando ele não referencia o contato informado.
 */
export const mesclarContatoEmTicket = (oTicket, oContato) => {
  if (!oTicket || !oContato?.id) return oTicket
  const oContatoAnterior = oTicket.contact
  if (!oContatoAnterior || oContatoAnterior.id !== oContato.id) return oTicket

  const oContatoMesclado = { ...oContatoAnterior, ...oContato }
  oContatoMesclado.name = oContato.name ?? oContato.nome ?? oContatoAnterior.name ?? oContatoAnterior.nome
  oContatoMesclado.nome = oContato.nome ?? oContato.name ?? oContatoAnterior.nome ?? oContatoAnterior.name
  oContatoMesclado.profilePicUrl =
    oContato.profilePicUrl ?? oContato.imagemPerfil ?? oContatoAnterior.profilePicUrl ?? oContatoAnterior.imagemPerfil
  oContatoMesclado.imagemPerfil =
    oContato.imagemPerfil ?? oContato.profilePicUrl ?? oContatoAnterior.imagemPerfil ?? oContatoAnterior.profilePicUrl

  return { ...oTicket, contact: oContatoMesclado }
}

export const normalizarTextoPesquisa = (cTexto) => (cTexto || "").trim().toLowerCase()

/** Filas permitidas ao usuário (null só com allTicket enabled ou fila virtual Sem Fila). */
export const filasUsuarioPermitidasCalcular = (user) => {
  const mFilas = Array.isArray(user?.queues) ? user.queues.map((oFila) => oFila.id) : []

  if (mFilas.includes(null)) {
    return mFilas
  }

  if (user?.allTicket === "enabled") {
    return [...mFilas, null]
  }

  return mFilas
}

/** Intersecta filas salvas (localStorage/UI) com as filas permitidas ao usuário. */
export const filasUsuarioSanitizar = (mFilasSalvas, user) => {
  if (!Array.isArray(mFilasSalvas) || mFilasSalvas.length === 0) {
    return []
  }

  if (user?.profile === "admin") {
    return mFilasSalvas
  }

  const mFilasPermitidas = filasUsuarioPermitidasCalcular(user)

  return mFilasSalvas.filter((nFilaId) =>
    mFilasPermitidas.some((nFilaPermitida) => nFilaId === nFilaPermitida),
  )
}

export const ordenarPorUltimaMensagemData = (mTickets) => {
  return [...mTickets].sort((a, b) => {
    const nA = new Date(a?.ultimaMensagemData || a?.updatedAt || 0).getTime()
    const nB = new Date(b?.ultimaMensagemData || b?.updatedAt || 0).getTime()
    return nB - nA
  })
}

export const usuarioTemAcessoAoTicket = ({
  ticket,
  user,
  oFilasSelecionadas = [],
  oFilasDoUserLogado = [],
  profile,
  isUserPermitted = () => false,
}) => {
  if (!ticket || !user) return false
  if (profile === "admin") return true

  if (ticket.canalTipoId === canais.interno) {
    const lEhParticipanteDireto = ticket.responsavelId === user.id || ticket.usuarioId === user.id
    if (lEhParticipanteDireto) return true
    return Array.isArray(ticket.conversaParticipantes)
      ? ticket.conversaParticipantes.some((p) => p.usuarioId === user.id)
      : false
  }

  // Supervisor também acessa conversas externas atribuídas aos usuários permitidos pela hierarquia de perfil.
  if (ticket.responsavelId != null && ticket.responsavelId !== user.id) {
    if (profile !== "supervisor" || !isUserPermitted(ticket.responsavelId)) return false
  }

  const lFilaSelecionadaOk = oFilasSelecionadas.length === 0 || oFilasSelecionadas.includes(ticket.queueId)
  const lSemFilaExplicita = ticket.queueId === null
  const lAcessoFila =
    oFilasDoUserLogado.includes(ticket.queueId) || (lSemFilaExplicita && user.allTicket === "enabled")

  return lFilaSelecionadaOk && lAcessoFila
}

/**
 * Conversas internas só aparecem sem filtros de Fila, Situação ou Motivo,
 * ou quando o canal "Interno" é selecionado explicitamente.
 */
export const filtrosEspecificosAtivosExcluemInterno = ({
  oFilasSelecionadas = [],
  oSituacoesSelecionadas = [],
  oMotivosSelecionados = [],
  canalSelecionado = null,
}) => {
  if (canalSelecionado?.id === canais.interno) return false

  return (
    oFilasSelecionadas.length > 0 ||
    oSituacoesSelecionadas.length > 0 ||
    oMotivosSelecionados.length > 0
  )
}

/**
 * Decide se o usuário logado deve receber alerta (som/notificação do navegador) para uma
 * mensagem nova de determinado ticket. Reutiliza `usuarioTemAcessoAoTicket` para não duplicar
 * a regra de assignee/fila/admin/supervisor — aqui não existe filtro de fila selecionada,
 * pois notificação não é escopada por filtro de tela.
 * Cobre conversas WhatsApp/externas e internas; supressões de UX (ticket já aberto, rota
 * atual, validação de perfil da mensagem) ficam fora do helper, por serem responsabilidade
 * do componente que dispara a notificação.
 */
export const notificacaoConversaUsuarioDeveAlertar = ({
  ticket,
  user,
  oFilasDoUserLogado = [],
  isUserPermitted = () => false,
}) =>
  usuarioTemAcessoAoTicket({
    ticket,
    user,
    oFilasDoUserLogado,
    profile: user?.profile,
    isUserPermitted,
  })

export const ticketPassaNosFiltros = ({
  ticket,
  filtros,
  ehSemResponsavel = false,
  user,
  oFilasDoUserLogado = [],
  isUserPermitted = () => false,
}) => {
  if (!ticket) return false

  const {
    oFilasSelecionadas = [],
    oSituacoesSelecionadas = [],
    oUsuariosSelecionados = [],
    oContatosSelecionados = [],
    oMotivosSelecionados = [],
    canalSelecionado = null,
    cSearchParam = "",
  } = filtros || {}

  const lAcesso = usuarioTemAcessoAoTicket({
    ticket,
    user,
    oFilasSelecionadas,
    oFilasDoUserLogado,
    profile: user?.profile,
    isUserPermitted,
  })

  if (!lAcesso) return false

  const lEhConversaInterna = ticket.canalTipoId === canais.interno

  if (
    lEhConversaInterna &&
    filtrosEspecificosAtivosExcluemInterno({
      oFilasSelecionadas,
      oSituacoesSelecionadas,
      oMotivosSelecionados,
      canalSelecionado,
    })
  ) {
    return false
  }

  // Admin: bypass fila/responsável só quando o filtro correspondente não está ativo na UI.
  const lAdminBypassFila = user?.profile === "admin" && oFilasSelecionadas.length === 0
  const lAdminBypassUsuario = user?.profile === "admin" && oUsuariosSelecionados.length === 0

  const lMatchesQueue =
    lAdminBypassFila ||
    (oFilasSelecionadas.length === 0
      ? oFilasDoUserLogado.includes(ticket.queueId) ||
        (ticket.queueId === null && user?.allTicket === "enabled")
      : (ticket.queueId === null && oFilasSelecionadas.includes(null)) ||
        (ticket.queueId && oFilasSelecionadas.includes(ticket.queueId)))

  const lMatchesSituacao =
    oSituacoesSelecionadas.length === 0 || (ticket.situacaoId && oSituacoesSelecionadas.includes(ticket.situacaoId))

  const lUsuarioEhParticipante = lEhConversaInterna && (ticket.responsavelId === user?.id || ticket.usuarioId === user?.id)

  // Sem filtro de usuário: inclui Sem Responsável (responsavelId nulo). Caso contrário
  // isUserPermitted(null) falha e o card some do Kanban no desassumir via socket.
  const lMatchesUsers = ehSemResponsavel
    ? ticketEhSemResponsavelAtivo(ticket)
    : lAdminBypassUsuario
      ? true
      : oUsuariosSelecionados.length === 0
        ? ticketEhSemResponsavelAtivo(ticket) ||
          lUsuarioEhParticipante ||
          isUserPermitted(ticket.responsavelId)
        : ticket.responsavelId && oUsuariosSelecionados.includes(ticket.responsavelId)

  const lMatchesContacts =
    oContatosSelecionados.length === 0 ||
    (ticket.contact && ticket.contact.id && oContatosSelecionados.includes(ticket.contact.id))

  const lMatchesMotivos =
    oMotivosSelecionados.length === 0 ||
    (ticket.motivoContatoId && oMotivosSelecionados.includes(ticket.motivoContatoId))

  const lMatchesCanal = !canalSelecionado?.id || ticket.canalTipoId === canalSelecionado.id

  const lMatchesBase =
    lMatchesQueue && lMatchesSituacao && lMatchesUsers && lMatchesContacts && lMatchesMotivos && lMatchesCanal

  const cSearch = normalizarTextoPesquisa(cSearchParam)
  if (!cSearch) return lMatchesBase

  const cContato = normalizarTextoPesquisa(ticket.contact?.name)
  const cMensagem = normalizarTextoPesquisa(ticket.ultimaMensagem)
  const lMatchesSearch = cContato.includes(cSearch) || cMensagem.includes(cSearch)

  return lMatchesBase && lMatchesSearch
}

export const addIdToTop = (mIds, nId) => [nId, ...mIds.filter((id) => id !== nId)]

export const insertIdAtIndex = (mIds, nId, nIndex) => {
  const mIdsSemTicket = mIds.filter((id) => id !== nId)
  const nIdx = Math.max(0, Math.min(nIndex ?? 0, mIdsSemTicket.length))
  const mIdsAntes = mIdsSemTicket.slice(0, nIdx)
  const mIdsDepois = mIdsSemTicket.slice(nIdx)
  return [...mIdsAntes, nId, ...mIdsDepois]
}

export const removeId = (mIds, nId) => mIds.filter((id) => id !== nId)

export const getTicketTimestamp = (ticket) =>
  new Date(ticket?.ultimaMensagemData || ticket?.updatedAt || ticket?.createdAt || 0).getTime()

export const sortIdsByRecent = (ids, ticketsById) =>
  [...ids].sort((idA, idB) => getTicketTimestamp(ticketsById[idB]) - getTicketTimestamp(ticketsById[idA]))

/** Ordena IDs do mais antigo para o mais novo (Kanban). */
export const sortIdsByOldest = (ids, ticketsById) =>
  [...ids].sort((idA, idB) => getTicketTimestamp(ticketsById[idA]) - getTicketTimestamp(ticketsById[idB]))

/**
 * Índice de inserção cronológica (mais antigo → mais novo) na coluna,
 * para auto-alinhar cards arrastados com a ordenação por idade.
 */
export const indiceInsercaoPorIdade = (mIds, nId, ticketsById) => {
  const nTimestamp = getTicketTimestamp(ticketsById[nId])
  const mIdsSemTicket = mIds.filter((id) => id !== nId)
  const nIdx = mIdsSemTicket.findIndex((id) => getTicketTimestamp(ticketsById[id]) > nTimestamp)
  return nIdx === -1 ? mIdsSemTicket.length : nIdx
}

export const mergeIdsForPage = (existingIds, incomingTickets, page) => {
  const incomingIds = incomingTickets.map((ticket) => ticket.id)
  if (page === 1) return incomingIds
  const existingSet = new Set(existingIds)
  const appended = incomingIds.filter((id) => !existingSet.has(id))
  return [...existingIds, ...appended]
}

/**
 * Mescla página da coluna Kanban priorizando a ordem do servidor (ultimaMensagemData ASC).
 * Mantém tickets locais ainda não refletidos no servidor (drag recente / API pendente)
 * e reordena todos do mais antigo para o mais novo.
 */
export const mergeKanbanColumnIdsPreservingOrder = (existingIds, incomingTickets, page, ticketsById, cSituacaoKey) => {
  const incomingIds = incomingTickets.map((ticket) => ticket.id)
  if (page !== 1) {
    return sortIdsByOldest(mergeIdsForPage(existingIds, incomingTickets, page), ticketsById)
  }
  if (!existingIds?.length) {
    return incomingIds
  }
  const incomingSet = new Set(incomingIds)
  const localOnly = existingIds.filter((id) => {
    if (incomingSet.has(id)) return false
    const oTicket = ticketsById[id]
    return oTicket && String(oTicket.situacaoId) === cSituacaoKey
  })
  return sortIdsByOldest([...incomingIds, ...localOnly], ticketsById)
}

/** page 1 com substituir: usa só resposta do servidor (ex.: troca de filtros). */
export const resolverIdsColunaKanban = (
  existingIds,
  incomingTickets,
  page,
  ticketsById,
  cSituacaoKey,
  lSubstituirColuna = false,
) => {
  const incomingIds = incomingTickets.map((ticket) => ticket.id)
  if (lSubstituirColuna && page === 1) {
    return incomingIds
  }
  return mergeKanbanColumnIdsPreservingOrder(existingIds, incomingTickets, page, ticketsById, cSituacaoKey)
}

/**
 * Count da coluna após fetch: preserva ajustes otimistas de drag quando o servidor ainda está desatualizado.
 */
export const calcularCountColunaKanban = ({
  nCountAnterior,
  nIdsAntes,
  nIdsDepois,
  nCountServidor,
  page,
  lHasMore,
}) => {
  const nServer = Number(nCountServidor ?? nIdsDepois)
  if (!lHasMore) {
    return nIdsDepois
  }
  if (page !== 1 || !nIdsAntes) {
    return nServer
  }
  if (nCountAnterior != null) {
    const nDeltaIds = nIdsDepois - nIdsAntes
    return Math.max(0, nCountAnterior + nDeltaIds)
  }
  return nServer
}

/** Preserva ordem manual de tickets no Fluxo ao revalidar coluna. */
export const mergeFluxoTicketsPreservingOrder = (mTicketsExistentes, mTicketsIncoming, nMotivoId) => {
  if (!mTicketsExistentes?.length) return mTicketsIncoming

  const oIncomingPorId = new Map(mTicketsIncoming.map((oTicket) => [oTicket.id, oTicket]))
  const mIdsIncoming = new Set(mTicketsIncoming.map((oTicket) => oTicket.id))

  const ticketPermaneceNaColuna = (oTicket) =>
    mIdsIncoming.has(oTicket.id) || Number(oTicket.motivoContatoId) === Number(nMotivoId)

  const mTicketsMantidos = mTicketsExistentes
    .map((oTicket) => oIncomingPorId.get(oTicket.id) ?? oTicket)
    .filter(ticketPermaneceNaColuna)

  const mIdsExistentes = new Set(mTicketsExistentes.map((oTicket) => oTicket.id))
  const mTicketsNovos = mTicketsIncoming.filter((oTicket) => !mIdsExistentes.has(oTicket.id))

  return [...mTicketsMantidos, ...mTicketsNovos]
}

export const fluxoKanbanQueryKey = (nMotivoId, cFiltrosKey) => ["fluxo", "kanban", nMotivoId, cFiltrosKey]

/**
 * Calcula novo estado local após mover card entre colunas (motivos) no Fluxo.
 * Retorna null se o ticket não for encontrado.
 */
export const computeMovimentoFluxoColuna = (prev, fromMotivoId, toMotivoId, cardId, nIndex = 0) => {
  const novoEstado = { ...prev }
  const fromId = fromMotivoId.toString()
  const toId = toMotivoId.toString()
  const cCardId = cardId.toString()

  let cColunaOrigem = fromId
  let ticketEncontrado = novoEstado[fromId]?.tickets.find((t) => t.id.toString() === cCardId)

  if (!ticketEncontrado) {
    cColunaOrigem = Object.keys(novoEstado).find((cMotivoId) =>
      novoEstado[cMotivoId]?.tickets.some((t) => t.id.toString() === cCardId),
    )
    if (cColunaOrigem) {
      ticketEncontrado = novoEstado[cColunaOrigem].tickets.find((t) => t.id.toString() === cCardId)
    }
  }

  if (!ticketEncontrado) return null

  if (novoEstado[cColunaOrigem]) {
    novoEstado[cColunaOrigem] = {
      ...novoEstado[cColunaOrigem],
      tickets: novoEstado[cColunaOrigem].tickets.filter((t) => t.id.toString() !== cCardId),
      count: Math.max(0, (novoEstado[cColunaOrigem].count ?? 0) - 1),
    }
  }

  if (!novoEstado[toId]) {
    novoEstado[toId] = { tickets: [], count: 0 }
  }

  const mTicketsDestino = (novoEstado[toId].tickets || []).filter((t) => t.id.toString() !== cCardId)
  const oTicketMovido = { ...ticketEncontrado, motivoContatoId: Number(toMotivoId) }
  const nIdx = Math.max(0, Math.min(nIndex ?? 0, mTicketsDestino.length))
  mTicketsDestino.splice(nIdx, 0, oTicketMovido)

  novoEstado[toId] = {
    ...novoEstado[toId],
    tickets: mTicketsDestino,
    count: cColunaOrigem === toId ? novoEstado[toId].count : (novoEstado[toId].count ?? 0) + 1,
  }

  return {
    novoEstado,
    cColunaOrigem,
    toId,
    cCardId,
    nIdx,
    oTicketMovido,
  }
}

/** Mantém cache React Query alinhado ao estado local após drag (evita flicker ao remontar a tela). */
export const aplicarMovimentoFluxoQueryCache = (
  queryClient,
  cFiltrosKey,
  { cColunaOrigem, toId, cCardId, nIdx, oTicketMovido },
) => {
  if (!queryClient || !cFiltrosKey) return

  const patchColuna = (motivoId, fn) => {
    const nMotivoId = Number(motivoId)
    queryClient.setQueryData(fluxoKanbanQueryKey(nMotivoId, cFiltrosKey), (old) => {
      if (!old) return old
      return fn(old)
    })
  }

  patchColuna(cColunaOrigem, (old) => {
    const tickets = (old.tickets || []).filter((t) => t.id.toString() !== cCardId)
    return {
      ...old,
      tickets,
      count: Math.max(0, (old.count ?? tickets.length) - 1),
    }
  })

  patchColuna(toId, (old) => {
    const base = old || {
      motivoId: Number(toId),
      tickets: [],
      count: 0,
      hasMore: false,
    }
    const tickets = (base.tickets || []).filter((t) => t.id.toString() !== cCardId)
    tickets.splice(nIdx, 0, oTicketMovido)
    const lMesmaColuna = cColunaOrigem === toId
    return {
      ...base,
      motivoId: Number(toId),
      tickets,
      count: lMesmaColuna ? base.count ?? tickets.length : (base.count ?? 0) + 1,
    }
  })
}

export const mergeTicketsIntoKanban = (kanbanIdsBySituacao, tickets, ticketsById) => {
  const next = { ...kanbanIdsBySituacao }
  tickets.forEach((ticket) => {
    if (!ticket?.id) return
    Object.keys(next).forEach((situacaoId) => {
      next[situacaoId] = removeId(next[situacaoId] || [], ticket.id)
    })
    if (ticket.situacaoId != null) {
      const key = String(ticket.situacaoId)
      next[key] = [...(next[key] || []), ticket.id]
    }
  })

  Object.keys(next).forEach((situacaoId) => {
    next[situacaoId] = sortIdsByOldest(next[situacaoId] || [], ticketsById)
  })
  return next
}

export const computeUnreadCounter = (ticketsById, openIds, userId) =>
  openIds.reduce((acc, id) => {
    const t = ticketsById[id]
    if (!t) return acc
    return acc + (calcularMensagensNaoLidas(t, userId) > 0 ? 1 : 0)
  }, 0)
